import { Component, inject, OnInit, Injector, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, of, tap} from 'rxjs';

import {
  BaseSearchOptions,
  InvoiceClient,
  InvoiceContractFilterOptions,
  InvoiceContractSearchOptions,

} from '@core/api';

import { ITableConfig} from '@shared/components/table-base/table-base.types';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { ModalFilterComponent } from '@shared/components/modal-filter/modal-filter.component';
import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { MenuItem } from '@affinis/smartus-components/lib/menu-extra/menu-extra.component';
import { InvoiceTenantViewModel } from '@core/api/smart-portal.api';

const filterOptionsInvoiceContractsKey = 'filterOptionsInvoiceContracts';

@Component({
  selector: 'app-contract-list',
  templateUrl: './contract-list.component.html',
  styleUrls: [
    '../../../../../app/shared/components/table-base/table-base.scss',
    './contract-list.component.scss'
  ],
  standalone: false,
})
export class ContractListComponent   extends TableBaseComponent  implements OnInit
{
  title: string;

  tenantName: string;

  modalComponent = ModalFilterComponent;

  loading = false;

  override tableIdentifier = 'eInvoiceContractList';

  override columns: MtxGridColumn[] = [];

  chips: Chip[] = [];

  menuItems: MenuItem[] = [];

  dialogData: any;

  override filterFields: Array<FormlyFieldConfig> = [];

  constructor(
    injector: Injector,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly invoiceClient: InvoiceClient,
    private readonly sessionStorageService: SessionStorageService,
    private readonly locationFormlyFieldService: LocationFormlyFieldService,
  ) {
    super(injector);

    this.config.grid.paging.pageSize = 10;
    this.query.pagingOptions.take = this.config.grid.paging.pageSize;
  }

  override config = this.configure(<ITableConfig>{
    title: 'title',
    translatePath: 'invoice.tenant_contracts',
  });  

  ngOnInit(): void {

    this.title = this.translate(this.config.title);

    this.breadcrumbService.setBreadcrumbs([{ label: this.title }]); 
    
    this.columns = [
      {
        header: this.translate('label__contractID'),
        field: 'contractId',
        sortable: true,
        class: '',
        resizable: true,
        minWidth: 30,
        width: "200px"
      },
      {
        header: this.translate('label__newInvoiceCount'),
        field: 'newInvoiceCount',
        sortable: true,
        class: '',
        resizable: true,
        minWidth: 30,
        width: "200px"
      },
      {
        header: this.translate('label__contractName'),
        field: 'contractName',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 50
      },
      {
        header: this.translate('label__contractAddress'),
        field: 'contractAddress',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 50
      }
      ];

    this.filterFields = this.getFilterFields();

    this.loadColumnVisibilityAndOrder();

    this.loadTenantName();

    let filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
    this.config.grid.filter.filterOptions = { ...filterOptions };

    this.mapFormModelToChips(filterOptions);

    this.dialogData = {
      columns: this.columns,
      filterFields: this.filterFields,
      filterValues: this.sessionStorageService.get(filterOptionsInvoiceContractsKey),
      actions: {
        onSubmit: (formModel: any) => {
          this.sessionStorageService.set(filterOptionsInvoiceContractsKey, formModel);

          filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips(formModel);
          this.updateFilter({
            ...formModel
          });

          this.refreshFromFilter();
          this.dialogData.filterValues =
            this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
        },
        onReset: () => {
          this.sessionStorageService.set(filterOptionsInvoiceContractsKey, {});
          this.resetLocationSelection();

          filterOptions = this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips({});

          this.refreshFromFilter();
        },
      },
    };    
  }

  override getTableData$ = (
      query?: BaseSearchOptions,
      filterOptions?: InvoiceContractFilterOptions
      ) => {
  
      const searchOptions: InvoiceContractSearchOptions = {
        ...query,
        filterOptions
      };
  
      this.closeToast();

      return this.invoiceClient.getCustomerContracts(searchOptions).pipe(
          tap(res => {
          }),
           catchError(() => {
  
          this.activeToast = this.toastr.error(
            this.translateService.instant(
              'crud.details.error.failed_to_load_msg'
            ),
            this.translateService.instant(
              'crud.details.error.failed_to_load_title'
            ),
            { timeOut: 0 }
          );
  
          return of([]);
        })
      );
    };

    controlButtons: ButtonProps[] = [
      {
        label: this.translateService.instant('crud.refresh'),
        onClick: () => this.refresh(),
      },
    ];

  onCloseDialog = (e: any) => {
    this.resetLocationSelection();
  };

  loadTenantName(): void {

    this.invoiceClient.getTenant().subscribe({
      next: (tenant: InvoiceTenantViewModel) => {
        this.tenantName = !tenant?.companyName.trim() ? `${tenant?.firstName} ${tenant?.lastName}` : tenant?.companyName;
      },
      error: (err) => {
        this.router.navigate(['/no-access'], { skipLocationChange: true });
      }
    });
  }

  resetLocationSelection() {
    this.locationFormlyFieldService.resetSelection();
    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsInvoiceContractsKey);
  }

  getFilterFields() {
    
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            type: 'input',
            key: 'contractId',
              props: {
              label: this.translate('label__contractID'),
            }
          },
          {
            type: 'input',
            key: 'contractName',
              props: {
              label: this.translate('label__contractName'),
            }
          },
          {
            type: 'input',
            key: 'contractAddress',
              props: {
              label: this.translate('label__contractAddress'),
            }
          },
          {
            type: 'checkbox',
            key: 'hasNewInvoices',
              props: {
              label: this.translate('label__hasNewInvoices'),
            }
          }
        ]
      }
    ];
  }

  override clickRow = (data: any, router: Router, route: ActivatedRoute) => {

    this.router.navigateByUrl('/e-invoice/invoices', { 
      state:
        {
          contractId: data.rowData.contractId, 
          tenantName: this.tenantName,
          contractName: data.rowData.contractName
        }
       });
  };

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.grid.filter.filterOptions[eventObj.id];

    this.sessionStorageService.set(filterOptionsInvoiceContractsKey, {
      ...this.config.grid.filter.filterOptions,
      ticketClassList: this.config.grid.filter.filterOptions.ticketClassList
        ? this.sessionStorageService.get(filterOptionsInvoiceContractsKey).ticketClassList
        : null,
    });

    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsInvoiceContractsKey);

    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.refreshFromFilter();
  };

  mapFormModelToChips = formModel => {
    this.chips = [];
    this.chips.length = 0;

    Object.keys(formModel).forEach(formModelItemKey => {
      const flattened = this.filterFields[0].fieldGroup?.flatMap(field =>
        field.fieldGroup ? [field, ...field.fieldGroup] : field
      );
      const field = flattened?.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });

      if (formModelItemKey === 'hasNewInvoices') {
          let hasNewInvoices: boolean | undefined = formModel[formModelItemKey];
          if (hasNewInvoices !== undefined) {
            this.chips.push({
              id: formModelItemKey,
              label: hasNewInvoices ? this.translate('label__hasNewInvoices') : this.translate('label__hasNoNewInvoices'),
            });
          }
      }
      else if (formModel[formModelItemKey]) {
        this.chips.push({
          id: formModelItemKey,
          label: `${field.props.label}: ${formModel[formModelItemKey]}`,
        });
      } 
    });
  };

      isFilterActive() : boolean {
      const filterOptions = this.config.grid.filter.filterOptions;
      return filterOptions && Object.keys(filterOptions).length > 0;
    }

    get noResultText(): string {
      if (this.isFilterActive()) {
        return this.translateService.instant('invoice.tenant_contracts.no_result_text_filtered');
      }
      return this.translateService.instant('invoice.tenant_contracts.no_result_text');
    }
 }

